import React, { useState, useMemo, useEffect } from 'react';
import { BanknotesIcon, CheckCircleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { ensureFacturas } from '../utils/fianzaEventos';
import { useComisionLedger, useComisionConfig, useConvenios } from '../contexts/DataContext';
import { settleComision } from '../api/comisiones';
import { resolveParticipacion } from '../utils/calculations';
import { ComisionEditorDialog } from './ComisionEditorDialog';
import TablePager from './TablePager';

const MOVS_POR_PAGINA = 8;
const HISTORIAL_POR_PAGINA = 6;

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);
const fmtDate = (ms) => (ms ? new Date(Number(ms)).toLocaleDateString('es-MX') : '');

const COBRO_LABEL = {
  cobrada: 'Cobrada',
  pendiente: 'Pendiente',
  neutralizada: 'Neutralizada',
  adeudo: 'Adeudo',
  adeudo_liquidado: 'Adeudo liquidado',
};

// A reversal never becomes "cobrada": it either cancelled out a comisión that was
// never collected (neutralizada, nothing owed) or it is a debt to the afianzadora
// that gets repaid or netted (adeudo → adeudo_liquidado).
const ESTADOS_REVERSA = ['neutralizada', 'adeudo', 'adeudo_liquidado'];
const isReversa = (m) =>
  m?.naturaleza === 'reversa' ||
  ESTADOS_REVERSA.includes(m?.estadoCobro) ||
  (Number(m?.comisionMonto) || 0) < 0;

const TIPO_LABELS = {
  emision: 'Emisión',
  adicional: 'Adicional',
  sustituye: 'Sustituye',
  cargo: 'Cargo',
};

/**
 * ComisionLedgerPanel — owner-only settlement surface for a single fianza.
 *
 * One compact table over the fianza's comisión movements (orgs/{org}/comisiones,
 * owner-readable via rules): what each movement pays, off which factura, and its
 * settlement state, with the action inline. Factura `pagada` itself is managed
 * in the payment card above; here it only gates "Marcar cobrada" — the backend
 * rejects the cobro while the source factura is unpaid.
 */
export function ComisionLedgerPanel({ fianza, contratoData, userData, onUpdate }) {
  const isOwner = userData?.rol === 'owner';
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  // % and participación are corrected together in one editor — they are the
  // same question ("what do I earn here?") and are usually learned at the same
  // moment, when the afianzadora's payout arrives.
  const [editorOpen, setEditorOpen] = useState(false);
  const { config: comisionConfig } = useComisionConfig();
  const { conveniosById } = useConvenios();

  // The whole org ledger comes from the single DataContext live listener; filter
  // to this fianza locally. No per-component Firestore read.
  const { movements: allMovements } = useComisionLedger();
  const facturas = ensureFacturas(fianza);
  const movements = useMemo(
    () => allMovements.filter((m) => m.fianzaId === fianza?.id),
    [allMovements, fianza?.id]
  );
  const [movPage, setMovPage] = useState(1);
  const [histPage, setHistPage] = useState(1);
  useEffect(() => {
    setMovPage(1);
    setHistPage(1);
  }, [fianza?.id]);

  // Derived activity feed, newest first: every transition already leaves a
  // timestamp on the movement (booking, factura pagada, % definido, retarifa,
  // marcador de convenio, cobro, adeudo) — no extra writes, just composition.
  const historial = useMemo(() => {
    const porFactura = Object.fromEntries(facturas.map((f) => [f.id, f]));
    const eventos = [];
    const facturaVista = new Set();
    movements.forEach((m) => {
      const etiqueta = TIPO_LABELS[m.tipo] || m.tipo;
      if (m.fecha) {
        eventos.push({ at: m.fecha, texto: `${etiqueta}: comisión registrada`, monto: m.comisionMonto });
      }
      const factura = m.facturaId ? porFactura[m.facturaId] : null;
      const pagada = factura ? Boolean(factura.pagada) : m.facturaPagada === true;
      const pagadaFecha = m.facturaPagadaFecha || factura?.pagadaFecha;
      if (pagada && pagadaFecha && m.facturaId && !facturaVista.has(m.facturaId)) {
        facturaVista.add(m.facturaId);
        eventos.push({ at: pagadaFecha, texto: 'Factura pagada: comisión cobrable', monto: m.comisionMonto });
      }
      if (m.override?.at) {
        eventos.push({
          at: m.override.at,
          texto: `% definido: ${m.comisionPct}% (antes ${m.override.anteriorPct != null ? `${m.override.anteriorPct}%` : 'sin %'})`,
          monto: m.comisionMonto,
        });
      }
      if (m.retarifa?.at) {
        eventos.push({
          at: m.retarifa.at,
          texto: `Retarificada: ${m.comisionPct}% (antes ${m.retarifa.anteriorPct != null ? `${m.retarifa.anteriorPct}%` : 'sin %'})`,
          monto: m.comisionMonto,
        });
      }
      if (m.splitAjuste?.at) {
        eventos.push({
          at: m.splitAjuste.at,
          texto: `Participación ajustada: ${m.splitPct}%${m.splitAjuste.anteriorPct != null ? ` (antes ${m.splitAjuste.anteriorPct}%)` : ''}`,
          monto: m.comisionMonto,
        });
      }
      if (m.splitCambio?.at) {
        eventos.push({
          at: m.splitCambio.at,
          texto: m.splitRol
            ? `Convenio del beneficiario aplicado (${m.splitPct}%)`
            : 'Convenio del beneficiario retirado',
          monto: m.comisionMonto,
        });
      }
      if (m.cobradaFecha) {
        eventos.push({ at: m.cobradaFecha, texto: `${etiqueta}: comisión cobrada`, monto: m.comisionMonto });
      }
      if (m.adeudoLiquidadoFecha) {
        eventos.push({ at: m.adeudoLiquidadoFecha, texto: 'Adeudo liquidado', monto: m.comisionMonto });
      }
    });
    return eventos.filter((e) => e.at).sort((a, b) => b.at - a.at);
  }, [movements, facturas]);

  if (!isOwner) return null;

  const facturasById = Object.fromEntries(facturas.map((f) => [f.id, f]));

  // Same vocabulary as the Comisiones page: a pendiente comisión is only
  // "cobrable" once its factura is pagada; before that it is not exigible.
  // The local facturas are the freshest source; the movement's denormalized
  // facturaPagada flag is the fallback.
  const movFacturaPagada = (m) => {
    const factura = m.facturaId ? facturasById[m.facturaId] : null;
    if (factura) return Boolean(factura.pagada);
    return m.facturaPagada === true;
  };

  const netComision = movements.reduce((s, m) => s + (Number(m.comisionMonto) || 0), 0);
  const netCobrada = movements
    .filter((m) => m.estadoCobro === 'cobrada')
    .reduce((s, m) => s + (Number(m.comisionMonto) || 0), 0);
  const netCobrable = movements
    .filter((m) => m.estadoCobro === 'pendiente' && (Number(m.comisionMonto) || 0) > 0 && movFacturaPagada(m))
    .reduce((s, m) => s + (Number(m.comisionMonto) || 0), 0);
  const netPorExigir = movements
    .filter((m) => m.estadoCobro === 'pendiente' && (Number(m.comisionMonto) || 0) > 0 && !movFacturaPagada(m))
    .reduce((s, m) => s + (Number(m.comisionMonto) || 0), 0);
  // Money already collected that has to go BACK to the afianzadora. Shown apart
  // from "pendiente" — it is a liability, not a receivable.
  const netAdeudo = movements
    .filter((m) => m.estadoCobro === 'adeudo')
    .reduce((s, m) => s + Math.abs(Number(m.comisionMonto) || 0), 0);

  // The emisión movement carries the fianza's % and split; the editor works
  // off it. A fianza whose ledger predates the backfill has no emisión booked —
  // its earliest ingreso movement (an endoso delta with the % pending) is the
  // anchor instead, and the backend accepts the override on it for exactly this
  // case. Participación in effect: the fianza's own negotiated override,
  // else convenio → org → 70/30.
  const emisionMov =
    movements.find((m) => m.tipo === 'emision') ||
    [...movements]
      .filter((m) => m.prima != null && !isReversa(m))
      .sort((a, b) => (a.fecha || 0) - (b.fecha || 0))[0] ||
    null;
  const convenioData = contratoData?.convenioId ? conveniosById?.[contratoData.convenioId] : null;
  const emisorNombre =
    fianza?.xmlFactura?.emisor?.nombre || fianza?.xmlFianza?.emisor?.nombre || '';
  const participacionActual =
    fianza?.participacionOverride?.beneficiario != null && fianza?.participacionOverride?.fiado != null
      ? fianza.participacionOverride
      : resolveParticipacion(emisorNombre, comisionConfig, convenioData);
  // Something to divide only when this fianza participates in a convenio split.
  const splitAplica = Boolean(
    contratoData?.convenioId || fianza?.beneficiarioConvenio || contratoData?.beneficiarioConvenio
  );

  const nextEstadoFor = (mov) => {
    if (isReversa(mov)) {
      if (mov.estadoCobro === 'adeudo') return 'adeudo_liquidado';
      if (mov.estadoCobro === 'adeudo_liquidado') return 'adeudo';
      return null; // neutralizada — nothing to settle
    }
    return mov.estadoCobro === 'cobrada' ? 'pendiente' : 'cobrada';
  };

  const toggleCobrada = async (mov) => {
    const next = nextEstadoFor(mov);
    if (!next) return;
    setBusyId(mov.id);
    setError(null);
    try {
      await settleComision(mov.id, next);
      // The DataContext live listener refreshes this row automatically.
    } catch (e) {
      console.error('settle failed', e);
      setError(e.message || 'No se pudo actualizar la comisión.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-4 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Header: title + the number the owner actually cares about */}
      <div className="flex items-center justify-between px-4 py-3">
        <h3 className="font-semibold text-gray-800 text-base flex items-center gap-2">
          <BanknotesIcon className="w-5 h-5 text-azul-surelia" />
          Comisión y liquidación
        </h3>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setEditorOpen(true)}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-azul-surelia/30 text-azul-surelia hover:bg-azul-surelia/5 transition-colors"
            title="Corregir el % de comisión y la participación de esta fianza"
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
            Ajustar comisión
          </button>
          <div className="text-right">
            <div className="text-xl font-bold text-azul-surelia">{fmtMXN(netComision)}</div>
            <div className="text-[11px] uppercase tracking-wide text-gray-400">Comisión neta</div>
          </div>
        </div>
      </div>

      {/* One-line settlement summary */}
      <div className="px-4 py-2.5 bg-zinc-50 border-y border-gray-100 flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span className="text-gray-500">
          Cobrada <span className="font-semibold text-emerald-700">{fmtMXN(netCobrada)}</span>
        </span>
        {netCobrable > 0 && (
          <span
            className="text-gray-500"
            title="Factura pagada y comisión sin marcar como cobrada — exigible ya."
          >
            Cobrable ahora <span className="font-semibold text-emerald-700">{fmtMXN(netCobrable)}</span>
          </span>
        )}
        {netPorExigir > 0 && (
          <span
            className="text-gray-500"
            title="La factura del cliente aún no se paga; la comisión existe pero todavía no es exigible."
          >
            Aún no exigible <span className="font-semibold text-amber-700">{fmtMXN(netPorExigir)}</span>
          </span>
        )}
        {netAdeudo > 0 && (
          <span
            className="text-gray-500"
            title="Comisión ya cobrada que debe devolverse a la afianzadora."
          >
            Adeudo <span className="font-semibold text-red-700">{fmtMXN(netAdeudo)}</span>
          </span>
        )}
        {movements.some((m) => m.comisionMonto == null) && (
          <span
            className="text-gray-500"
            title={movements.some((m) => m.splitRol === 'convenio')
              ? 'Movimientos sin % de comisión: no suman al neto hasta definir el % del agente del fiado.'
              : 'Movimientos sin % de comisión: no suman al neto hasta definir el %.'}
          >
            Sin % definido{' '}
            <span className="font-semibold text-amber-700">
              {movements.filter((m) => m.comisionMonto == null).length}
            </span>
          </span>
        )}
      </div>

      {error && (
        <div className="mx-4 mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
      )}

      {/* Movements */}
      {movements.length === 0 ? (
        <div className="px-4 py-5 text-sm text-gray-500">
          Aún no se ha registrado la comisión de esta fianza.
        </div>
      ) : (
        <table className="w-full text-sm leading-tight">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
              <th className="font-medium px-4 py-1">Movimiento</th>
              <th className="font-medium px-3 py-1">Fecha</th>
              <th className="font-medium px-3 py-1">Convenio</th>
              <th className="font-medium px-3 py-1">Factura</th>
              <th className="font-medium px-3 py-1 text-right">% Comisión</th>
              <th className="font-medium px-3 py-1 text-right">Comisión</th>
              <th className="font-medium px-4 py-1 text-right">Estado</th>
            </tr>
          </thead>
          <tbody>
            {[...movements]
              .sort((a, b) => (a.fecha || 0) - (b.fecha || 0))
              .slice((movPage - 1) * MOVS_POR_PAGINA, movPage * MOVS_POR_PAGINA)
              .map((m) => {
                const negative = (Number(m.comisionMonto) || 0) < 0;
                const factura = m.facturaId ? facturasById[m.facturaId] : null;
                const reversa = isReversa(m);
                const next = nextEstadoFor(m);
                const pctPendiente = m.comisionMonto == null;
                const canCobrar =
                  reversa || m.estadoCobro === 'cobrada' ||
                  (!pctPendiente && (!factura || factura.pagada));
                const blockedTitle = pctPendiente
                  ? 'Define el % de comisión antes de marcarla cobrada'
                  : 'La factura debe estar pagada primero';
                const actionLabel = reversa
                  ? (m.estadoCobro === 'adeudo' ? 'Marcar liquidado' : 'Reabrir adeudo')
                  : (m.estadoCobro === 'cobrada' ? 'Cobrada' : 'Marcar cobrada');
                return (
                  <tr key={m.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-1 whitespace-nowrap font-medium text-gray-800">
                      {TIPO_LABELS[m.tipo] || m.tipo}
                    </td>
                    <td className="px-3 py-1 whitespace-nowrap text-xs text-gray-400">
                      {m.fecha ? fmtDate(m.fecha) : '—'}
                    </td>
                    <td className="px-3 py-1 whitespace-nowrap">
                      {m.splitRol != null ? (
                        <span
                          className="inline-block text-[10px] px-1.5 py-0.5 rounded-full border border-azul-surelia/30 text-azul-surelia bg-azul-surelia/5"
                          title={m.splitRol === 'convenio'
                            ? `Fianza de convenio: recibes el ${m.splitPct}% de la comisión del agente del fiado.`
                            : `Beneficiario con convenio externo: conservas el ${m.splitPct}% de tu comisión.`}
                        >
                          {m.splitRol === 'convenio' ? `Convenio ${m.splitPct}%` : `Conv. externo ${m.splitPct}%`}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1 whitespace-nowrap">
                      {factura ? (
                        <span
                          className={`inline-flex items-center gap-1 ${factura.pagada ? 'text-emerald-700' : 'text-amber-700'}`}
                          title={factura.facturaId ? `#${factura.facturaId}` : undefined}
                        >
                          <span className={`w-2 h-2 rounded-full ${factura.pagada ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                          {factura.pagada ? 'Pagada' : 'Sin pagar'}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1 text-right tabular-nums whitespace-nowrap">
                      {m.comisionPct != null ? (
                        <span
                          className="inline-flex items-center gap-1 text-gray-700"
                          title={m.pctEstimado
                            ? 'Estimado con la tabla de comisiones por defecto; confírmalo cuando veas el % real.'
                            : m.manualOverride && m.override
                            ? `Editado: antes ${m.override.anteriorPct ?? '—'}%`
                            : undefined}
                        >
                          {m.comisionPct}%
                          {m.manualOverride && (
                            <span
                              className="text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200"
                              title={`% definido manualmente${m.override?.anteriorPct != null ? ` (antes ${m.override.anteriorPct}%)` : ''}.`}
                            >
                              confirmado
                            </span>
                          )}
                          {m.pctEstimado && (
                            <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">est.</span>
                          )}
                          {m.tipo === 'emision' && (
                            <button
                              type="button"
                              onClick={() => setEditorOpen(true)}
                              title="Corregir % y participación de esta fianza"
                              className="text-gray-300 hover:text-azul-surelia"
                            >
                              <PencilSquareIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </span>
                      ) : m.tipo === 'emision' ? (
                        <button
                          type="button"
                          onClick={() => setEditorOpen(true)}
                          title={m.splitRol === 'convenio'
                            ? 'El % del agente del fiado aún no se conoce; defínelo al verlo en la fianza emitida.'
                            : 'El % de comisión aún no se conoce; defínelo al verlo en la fianza emitida.'}
                          className="text-xs px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 hover:border-amber-500"
                        >
                          Definir %
                        </button>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className={`px-3 py-1 text-right tabular-nums font-semibold whitespace-nowrap ${negative ? 'text-red-600' : 'text-gray-800'}`}>
                      {pctPendiente ? (
                        <span className="text-xs font-medium text-amber-600">Por definir</span>
                      ) : (
                        <>
                          {m.splitRol != null && m.comisionBase != null &&
                            Number(m.comisionBase) !== Number(m.comisionMonto) && (
                            <span
                              className="text-[11px] font-normal text-gray-400 mr-1"
                              title="Comisión bruta antes de la participación del convenio"
                            >
                              {fmtMXN(m.comisionBase)} bruta
                              <span className="text-gray-300 mx-1">·</span>
                            </span>
                          )}
                          {fmtMXN(m.comisionMonto)}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-1 text-right">
                      {next ? (
                        <button
                          type="button"
                          disabled={busyId === m.id || !canCobrar}
                          onClick={() => toggleCobrada(m)}
                          title={!canCobrar ? blockedTitle : ''}
                          className={`inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full border transition-colors ${
                            m.estadoCobro === 'cobrada'
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                              : m.estadoCobro === 'adeudo'
                              ? 'bg-white text-red-700 border-red-300 hover:border-red-500'
                              : canCobrar
                              ? 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'
                              : 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed'
                          }`}
                        >
                          {m.estadoCobro === 'cobrada' ? (<><CheckCircleIcon className="w-3.5 h-3.5" /> Cobrada</>) : actionLabel}
                        </button>
                      ) : (
                        <span
                          className="text-xs text-gray-400"
                          title="Esta reversa canceló una comisión que nunca se cobró; no hay nada que liquidar."
                        >
                          {COBRO_LABEL[m.estadoCobro] || 'Sin saldo'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      )}
      {movements.length > MOVS_POR_PAGINA && (
        <div className="px-4">
          <TablePager
            total={movements.length}
            page={movPage}
            onPage={setMovPage}
            perPage={MOVS_POR_PAGINA}
            itemLabel="movimientos"
            compact
          />
        </div>
      )}

      <ComisionEditorDialog
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        fianza={fianza}
        contratoId={contratoData?.id || fianza?.contratoId}
        movimiento={emisionMov}
        participacion={participacionActual}
        showSplit={splitAplica}
        onSaved={onUpdate}
      />

      {/* Historial — the fianza's comisión activity, newest first */}
      {historial.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">Historial</div>
          <ol className="space-y-1.5">
            {historial
              .slice((histPage - 1) * HISTORIAL_POR_PAGINA, histPage * HISTORIAL_POR_PAGINA)
              .map((e, idx) => (
                <li key={`${e.at}-${idx}`} className="flex items-baseline gap-2 text-xs">
                  <span className="text-gray-400 tabular-nums shrink-0 w-20">{fmtDate(e.at)}</span>
                  <span className="text-gray-700">{e.texto}</span>
                  {e.monto != null && (
                    <span className="ml-auto tabular-nums text-gray-500 shrink-0">{fmtMXN(e.monto)}</span>
                  )}
                </li>
              ))}
          </ol>
          <TablePager
            total={historial.length}
            page={histPage}
            onPage={setHistPage}
            perPage={HISTORIAL_POR_PAGINA}
            itemLabel="eventos"
            compact
          />
        </div>
      )}
    </div>
  );
}
